import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

/**
 * Paylaşılan sidebar / mobil menü component'i.
 * Events.jsx'teki dropdown menü pattern'i baz alınarak çıkarılmıştır.
 * "Pیام‌ها" (Messages) item'ı `activeKey="messages"` ile aktif gösterilir.
 *
 * Kullanım:
 *   <MessagesSidebar activeKey="messages" />
 */
export const useSidebarMenu = () => {
    const { language } = useLanguage();

    const menuItems = [
        {
            id: 1,
            key: 'registration',
            title: language === 'fa' ? 'ثبت نام در نمایشگاه' : 'Exhibition Registration',
            hasSubmenu: true,
            subItems: [
                { key: 'finance', label: language === 'fa' ? 'امور مالی' : 'Financial Affairs' },
                { key: 'messages', label: language === 'fa' ? 'پیام ها' : 'Messages' }
            ]
        },
        {
            id: 2,
            key: 'account',
            title: language === 'fa' ? 'حساب کاربری' : 'User Account',
            hasSubmenu: false
        },
        {
            id: 4,
            key: 'logout',
            title: language === 'fa' ? 'خروج از حساب کاربری' : 'Logout',
            hasSubmenu: false
        }
    ];

    return { menuItems, language };
};

const SidebarMenuList = ({ menuItems, activeKey, onNavigate, size = 'desktop' }) => {
    const [activeMenu, setActiveMenu] = useState(
        menuItems.find((m) => m.hasSubmenu && m.subItems.some((s) => s.key === activeKey))?.id ?? null
    );

    const isMobile = size === 'mobile';
    const padX = isMobile ? 'px-3' : 'px-4';
    const padTop = isMobile ? 'pt-2.5' : 'pt-3';
    const padBottom = isMobile ? 'pb-2.5' : 'pb-3';
    const textSize = isMobile ? 'text-xs' : 'text-sm';
    const iconSize = isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4';
    const indentMargin = isMobile ? 'mx-2' : 'mx-3';

    const handleMenuClick = (item) => {
        if (item.hasSubmenu) {
            setActiveMenu((prev) => (prev === item.id ? null : item.id));
        } else if (item.key === 'logout') {
            onNavigate?.('logout');
        } else {
            onNavigate?.(item.key);
        }
    };

    return (
        <div className="bg-[#EBEBEB] rounded-2xl p-2 overflow-hidden">
            {menuItems.map((item, index) => (
                <div key={item.id}>
                    <div
                        onClick={() => handleMenuClick(item)}
                        className={`cursor-pointer transition-all duration-300 ease-in-out ${
                            activeMenu === item.id
                                ? 'bg-[#204286] text-white rounded-2xl'
                                : 'text-[#204286] hover:bg-gray-100 rounded-2xl'
                        } ${padX} ${padTop} font-medium ${textSize}`}
                    >
                        <div className={`flex items-center justify-between ${padBottom}`}>
                            <span>{item.title}</span>
                            {item.hasSubmenu && (
                                <svg
                                    className={`${iconSize} transition-all duration-300 ${
                                        activeMenu === item.id ? 'rotate-0' : 'rotate-180'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            )}
                        </div>

                        {item.hasSubmenu && activeMenu === item.id && (
                            <div className="animate-fadeIn border-t border-white/20">
                                <div className={`flex pt-2 ${padBottom}`}>
                                    <div className={`w-0.5 bg-white self-stretch ${indentMargin}`}></div>
                                    <div className="flex-1">
                                        {item.subItems.map((subItem) => (
                                            <div
                                                key={subItem.key}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onNavigate?.(subItem.key);
                                                }}
                                                className={`cursor-pointer transition-all duration-300 ease-in-out ${
                                                    isMobile ? 'py-1.5' : 'py-2'
                                                } ${textSize} ${
                                                    activeKey === subItem.key
                                                        ? 'text-white font-semibold'
                                                        : 'text-gray-300 hover:text-white'
                                                }`}
                                            >
                                                {subItem.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {index < menuItems.length - 1 && <div className="border-b border-white my-0.5"></div>}
                </div>
            ))}
        </div>
    );
};

const MessagesSidebar = ({ activeKey = 'messages', onNavigate }) => {
    const { menuItems, language } = useSidebarMenu();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <>
            {/* Mobil Menü Toggle */}
            <div className="lg:hidden mb-3">
                <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#EBEBEB] rounded-xl text-[#204286] font-bold text-xs"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    {language === 'fa' ? 'منو' : 'Menu'}
                </button>
            </div>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <motion.div
                            className="absolute right-0 top-0 h-full w-64 bg-white shadow-2xl"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-base font-bold text-[#204286]">
                                        {language === 'fa' ? 'منو' : 'Menu'}
                                    </h3>
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <SidebarMenuList
                                    menuItems={menuItems}
                                    activeKey={activeKey}
                                    onNavigate={(key) => {
                                        setIsMobileMenuOpen(false);
                                        onNavigate?.(key);
                                    }}
                                    size="mobile"
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Desktop Sidebar */}
            <div className="hidden lg:block w-[272px] shrink-0">
                <div className="sticky top-20">
                    <SidebarMenuList
                        menuItems={menuItems}
                        activeKey={activeKey}
                        onNavigate={onNavigate}
                        size="desktop"
                    />
                </div>
            </div>
        </>
    );
};

export default MessagesSidebar;